import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { queryClient } from '../api/queryClient';
import { useAuthStore, type AuthUser } from '../stores/authStore';

interface LoginResponse {
  accessToken: string;
  requiresTwoFactor: boolean;
  user: AuthUser;
}

export function useLogin() {
  const setLogin = useAuthStore((s) => s.login);
  return useMutation({
    mutationFn: async (input: {
      email: string;
      password: string;
      rememberMe: boolean;
    }): Promise<LoginResponse> => {
      const res = await apiClient.post<LoginResponse>('/auth/login', input);
      return res.data;
    },
    onSuccess: (data) => {
      // Clear stale anonymous/session-expired query state before
      // installing the freshly authenticated user. This preserves the
      // stale-cache hardening without clearing the bootstrap /me query
      // while AuthGuard is waiting for it to reject.
      queryClient.clear();
      setLogin(data.accessToken, data.user);
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  return useMutation({
    mutationFn: async () => {
      try {
        await apiClient.post('/auth/logout');
      } catch (err) {
        // Server-side logout failed — we still clear local state so the user is
        // not stuck "signed in" on the client. The next API call will 401 if
        // the server session is still alive somehow.
        console.warn('[auth] logout request failed', err);
      }
    },
    // Wipe React-Query cache BEFORE clearing the store so no queried
    // data from the prior session can resurface after re-login. Same
    // hardening as the refresh-failure branch in api/client.ts.
    onSuccess: () => {
      queryClient.clear();
      logout();
    },
  });
}
